import {
  CameraOutlined,
  DownloadOutlined,
  FontSizeOutlined,
  HolderOutlined,
  LineChartOutlined,
  RotateLeftOutlined,
  VerticalAlignMiddleOutlined,
  VerticalAlignTopOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { Divider, Dropdown, Menu, Space, Tooltip } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShopperType, ThreeRefs } from '../../types';
// Note: Debug mode for snapping and edge detection can be toggled with Shift+D keyboard shortcut
import './Toolbar.less';

import basketShopperImage from '../../../../../assets/fixtures/BasketShopper/front.png';
import cartShopperImage from '../../../../../assets/fixtures/CartShopper/front.png';
import { ReactComponent as QueueLineIcon } from '../../../../../assets/svgs/queue-line-icon.svg';

import { MAX_ZOOM, MIN_ZOOM } from '../../constants';

interface ToolbarProps {
  refs: ThreeRefs;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomToFit: () => void;
  onResetCamera: () => void;
  onAlignHorizontally: () => void;
  onAlignVertically: () => void;
  onAlignLeft: () => void;
  onAlignRight: () => void;
  onAlignTop: () => void;
  onAlignBottom: () => void;
  onAlignAdjacent: () => void;
  onAlignAdjacentVertical: () => void;
  onRotate: () => void;
  onAddText: () => void;
  onAddRectangle: () => void;
  onAddCircle: () => void;
  onTakeScreenshot: () => void;
  onDownloadRSF: () => void;
  isCameraControlsEnabled: boolean;
  onToggleCameraControls: () => void;
  onToggleQueueLineMode: () => void;
  onSelectShopperType: (type: ShopperType) => void;
  isInQueueLineMode: boolean;
  disableQueueLineMode: boolean;
  selectedShopperType: ShopperType;
  currentQueueLineColor?: string;
  onTogglePerformancePanel?: () => void;
}

const Toolbar: FC<ToolbarProps> = ({
  refs,
  onZoomIn,
  onZoomOut,
  onZoomToFit,
  onResetCamera,
  onAlignHorizontally,
  onAlignVertically,
  onAlignLeft,
  onAlignRight,
  onAlignTop,
  onAlignBottom,
  onAlignAdjacent,
  onAlignAdjacentVertical,
  onRotate,
  onAddText,
  onAddRectangle,
  onAddCircle,
  onTakeScreenshot,
  onDownloadRSF,
  isCameraControlsEnabled,
  onToggleCameraControls,
  onToggleQueueLineMode,
  onSelectShopperType,
  isInQueueLineMode,
  disableQueueLineMode,
  selectedShopperType,
  currentQueueLineColor,
  onTogglePerformancePanel,
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isAlignmentMenuVisible, setIsAlignmentMenuVisible] = useState(false);
  const [isShopperMenuVisible, setIsShopperMenuVisible] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(1);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [shopperTypes] = useState<
    { type: ShopperType; image: string; label: string }[]
  >([
    {
      type: ShopperType.WomanWithBasket,
      image: basketShopperImage,
      label: 'Woman with Basket',
    },
    {
      type: ShopperType.WomanWithCart,
      image: cartShopperImage,
      label: 'Woman with Cart',
    },
  ]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.drag-handle')) return;

      if (e.button !== 0) return; // Only handle left click
      setIsDragging(true);
      dragStartPos.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;

      if (toolbarRef.current) {
        const toolbar = toolbarRef.current;
        const bounds = toolbar.getBoundingClientRect();
        const maxX = window.innerWidth - bounds.width;
        const maxY = window.innerHeight - bounds.height;

        setPosition({
          x: Math.max(0, Math.min(maxX, newX)),
          y: Math.max(0, Math.min(maxY, newY)),
        });
      } else {
        setPosition({ x: newX, y: newY });
      }
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (toolbarRef.current) {
      toolbarRef.current.style.setProperty('--position-x', `${position.x}px`);
      toolbarRef.current.style.setProperty('--position-y', `${position.y}px`);
    }
  }, [position]);

  // Update current zoom level when camera changes
  useEffect(() => {
    const checkZoomLevel = () => {
      if (refs.camera.current) {
        const camera = refs.camera.current as THREE.OrthographicCamera;
        setCurrentZoom(camera.zoom);
      }
    };

    // Check initial zoom level
    checkZoomLevel();

    // Set up an interval to check zoom level
    const intervalId = setInterval(checkZoomLevel, 100);

    return () => clearInterval(intervalId);
  }, [refs]);

  const alignmentMenu = useMemo(
    () => (
      <Menu>
        <Menu.Item key="alignHorizontally" onClick={onAlignHorizontally}>
          <Space>
            <VerticalAlignMiddleOutlined className="menu-icon" />
            <span>Align Horizontal Centers</span>
          </Space>
        </Menu.Item>
        <Menu.Item key="alignVertically" onClick={onAlignVertically}>
          <Space>
            <VerticalAlignMiddleOutlined className="menu-icon rotate-90" />
            <span>Align Vertical Centers</span>
          </Space>
        </Menu.Item>
        <Menu.Item key="alignLeft" onClick={onAlignLeft}>
          <Space>
            <VerticalAlignTopOutlined className="menu-icon rotate-minus-90" />
            <span>Align Left</span>
          </Space>
        </Menu.Item>
        <Menu.Item key="alignRight" onClick={onAlignRight}>
          <Space>
            <VerticalAlignTopOutlined className="menu-icon rotate-90" />
            <span>Align Right</span>
          </Space>
        </Menu.Item>
        <Menu.Item key="alignTop" onClick={onAlignTop}>
          <Space>
            <VerticalAlignTopOutlined className="menu-icon" />
            <span>Align Top</span>
          </Space>
        </Menu.Item>
        <Menu.Item key="alignBottom" onClick={onAlignBottom}>
          <Space>
            <VerticalAlignTopOutlined className="menu-icon rotate-180" />
            <span>Align Bottom</span>
          </Space>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="alignAdjacent" onClick={onAlignAdjacent}>
          <Space>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="menu-icon"
            >
              <rect x="3" y="6" width="6" height="12" />
              <rect x="15" y="6" width="6" height="12" />
            </svg>
            <span>Align Adjacent (Horizontal)</span>
          </Space>
        </Menu.Item>
        <Menu.Item
          key="alignAdjacentVertical"
          onClick={onAlignAdjacentVertical}
        >
          <Space>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="menu-icon"
            >
              <rect x="6" y="3" width="12" height="6" />
              <rect x="6" y="15" width="12" height="6" />
            </svg>
            <span>Align Adjacent (Vertical)</span>
          </Space>
        </Menu.Item>
      </Menu>
    ),
    [
      onAlignHorizontally,
      onAlignVertically,
      onAlignLeft,
      onAlignRight,
      onAlignTop,
      onAlignBottom,
      onAlignAdjacent,
      onAlignAdjacentVertical,
    ]
  );

  const handleAlignmentMenuVisibleChange = (visible: boolean) => {
    setIsAlignmentMenuVisible(visible);
  };

  const shopperMenu = useMemo(() => {
    return (
      <Menu className="shopper-menu">
        {shopperTypes.map((shopperType) => (
          <Menu.Item
            key={shopperType.type}
            onClick={() => onSelectShopperType(shopperType.type)}
            className={
              selectedShopperType === shopperType.type ? 'selected-shopper' : ''
            }
            style={
              selectedShopperType === shopperType.type
                ? { background: '#8e44ad' }
                : {}
            }
          >
            <Space>
              <img
                src={shopperType.image}
                alt={shopperType.label}
                height={24}
                style={{ maxWidth: '24px' }}
              />
              <span
                style={
                  selectedShopperType === shopperType.type
                    ? { color: '#ffffff' }
                    : {}
                }
              >
                {shopperType.label}
              </span>
            </Space>
          </Menu.Item>
        ))}
      </Menu>
    );
  }, [
    isInQueueLineMode,
    shopperTypes,
    selectedShopperType,
    currentQueueLineColor,
    onSelectShopperType,
  ]);

  const handleShopperMenuVisibleChange = (visible: boolean) => {
    setIsShopperMenuVisible(visible);
  };

  return (
    <div
      ref={toolbarRef}
      className={`feb-top-menu ${isDragging ? 'dragging' : ''}`}
      onMouseDown={handleMouseDown}
    >
      <Space direction="horizontal" size="middle">
        <Tooltip title="Drag toolbar to move" mouseEnterDelay={0.5}>
          <HolderOutlined className="menu-icon drag-handle" />
        </Tooltip>
        <Divider type="vertical" className="divider" />
        <Tooltip
          title={
            currentZoom >= MAX_ZOOM ? 'Maximum zoom level reached' : 'Zoom In'
          }
          mouseEnterDelay={0.5}
        >
          <ZoomInOutlined
            onClick={onZoomIn}
            className={`menu-icon ${
              currentZoom >= MAX_ZOOM ? 'disabled-icon' : ''
            }`}
            style={{ opacity: currentZoom >= MAX_ZOOM ? 0.5 : 1 }}
          />
        </Tooltip>
        <Tooltip
          title={
            currentZoom <= MIN_ZOOM ? 'Minimum zoom level reached' : 'Zoom Out'
          }
          mouseEnterDelay={0.5}
        >
          <ZoomOutOutlined
            onClick={onZoomOut}
            className={`menu-icon ${
              currentZoom <= MIN_ZOOM ? 'disabled-icon' : ''
            }`}
            style={{ opacity: currentZoom <= MIN_ZOOM ? 0.5 : 1 }}
          />
        </Tooltip>
        <Tooltip title="Zoom to Fit" mouseEnterDelay={0.5}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="menu-icon lucide lucide-maximize"
            onClick={onZoomToFit}
            style={{ position: 'relative', top: '2px' }}
          >
            <path d="M8 3H5a2 2 0 0 0-2 2v3" />
            <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
            <path d="M3 16v3a2 2 0 0 0 2 2h3" />
            <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
          </svg>
        </Tooltip>

        <Divider type="vertical" className="divider" />
        <Tooltip
          title={
            isCameraControlsEnabled
              ? 'Disable Camera Controls'
              : 'Enable Camera Controls'
          }
          mouseEnterDelay={0.5}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`menu-icon ${
              isCameraControlsEnabled ? 'active-icon' : ''
            }`}
            onClick={onToggleCameraControls}
            style={{ position: 'relative', top: '2px' }}
          >
            <path d="M16.466 7.5C15.643 4.237 13.952 2 12 2 9.239 2 7 6.477 7 12s2.239 10 5 10c.342 0 .677-.069 1-.2" />
            <path d="m15.194 13.707 3.814 1.86-1.86 3.814" />
            <path d="M19 15.57c-1.804.885-4.274 1.43-7 1.43-5.523 0-10-2.239-10-5s4.477-5 10-5c4.838 0 8.873 1.718 9.8 4" />
          </svg>
        </Tooltip>
        <Tooltip title="Reset Camera" mouseEnterDelay={0.5}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="menu-icon lucide lucide-switch-camera"
            onClick={onResetCamera}
            style={{ position: 'relative', top: '2px' }}
          >
            <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
            <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
            <circle cx="12" cy="12" r="3" />
            <path d="m18 22-3-3 3-3" />
            <path d="m6 2 3 3-3 3" />
          </svg>
        </Tooltip>
        <Divider type="vertical" className="divider" />
        <Tooltip title="Add Text" mouseEnterDelay={0.5}>
          <FontSizeOutlined onClick={onAddText} className="menu-icon" />
        </Tooltip>
        <Tooltip title="Add Cube" mouseEnterDelay={0.5}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="menu-icon"
            onClick={onAddRectangle}
            style={{ position: 'relative', top: '2px' }}
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.29 7 12 12 20.71 7" />
            <line x1="12" y1="22" x2="12" y2="12" />
          </svg>
        </Tooltip>
        <Tooltip title="Add Sphere" mouseEnterDelay={0.5}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="menu-icon"
            onClick={onAddCircle}
            style={{ position: 'relative', top: '2px' }}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a15 15 0 0 0 0 20" />
            <path d="M2 12h20" />
          </svg>
        </Tooltip>
        <Divider type="vertical" className="divider" />
        <Tooltip title="Rotate 90°" mouseEnterDelay={0.5}>
          <RotateLeftOutlined onClick={onRotate} className="menu-icon" />
        </Tooltip>
        <Divider type="vertical" className="divider" />
        <Tooltip title="Alignment Options" mouseEnterDelay={0.5}>
          <Dropdown
            overlay={alignmentMenu}
            open={isAlignmentMenuVisible}
            onOpenChange={handleAlignmentMenuVisibleChange}
            trigger={['click']}
            placement="bottomLeft"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="menu-icon lucide lucide-align-start-vertical-icon lucide-align-start-vertical"
              style={{ position: 'relative', top: '2px', cursor: 'pointer' }}
            >
              <rect width="9" height="6" x="6" y="14" rx="2" />
              <rect width="16" height="6" x="6" y="4" rx="2" />
              <path d="M2 2v20" />
            </svg>
          </Dropdown>
        </Tooltip>
        <Divider type="vertical" className="divider" />

        {/* Queue Line Controls */}
        <Tooltip
          title={
            isInQueueLineMode ? 'Finish Queue Line' : 'Draw queue line shoppers'
          }
          mouseEnterDelay={0.5}
        >
          <div
            className={`menu-icon queue-line-icon ${
              isInQueueLineMode ? 'active-icon' : ''
            } ${disableQueueLineMode ? 'disabled-icon' : ''}`}
            onClick={disableQueueLineMode ? undefined : onToggleQueueLineMode}
          >
            <QueueLineIcon />
          </div>
        </Tooltip>

        {isInQueueLineMode && (
          <Dropdown
            overlay={shopperMenu}
            open={isShopperMenuVisible}
            onOpenChange={handleShopperMenuVisibleChange}
            trigger={['click']}
            placement="bottomLeft"
          >
            <div
              className="menu-icon shopper-selector"
              style={{ cursor: 'pointer' }}
            >
              <img
                src={
                  shopperTypes.find((st) => st.type === selectedShopperType)
                    ?.image
                }
                alt="Selected Shopper Type"
                style={{
                  border: `2px solid ${currentQueueLineColor || '#1890ff'}`,
                  background: '#fff',
                }}
              />
            </div>
          </Dropdown>
        )}

        <Divider type="vertical" className="divider" />
        <Tooltip title="Take Screenshot" mouseEnterDelay={0.5}>
          <CameraOutlined onClick={onTakeScreenshot} className="menu-icon" />
        </Tooltip>
        <Divider type="vertical" className="divider" />
        <Tooltip title="Download RSF" mouseEnterDelay={0.5}>
          <DownloadOutlined onClick={onDownloadRSF} className="menu-icon" />
        </Tooltip>
      </Space>

      <Divider
        type="vertical"
        className="divider"
        style={{ margin: '0 10px' }}
      />
      {onTogglePerformancePanel && (
        <Tooltip title="Toggle Performance Panel" mouseEnterDelay={0.5}>
          <LineChartOutlined
            onClick={onTogglePerformancePanel}
            className="menu-icon"
          />
        </Tooltip>
      )}
    </div>
  );
};

export default Toolbar;
